import { Lead } from '@services/leads/types'
import { Column } from '@ui-kit/Table/types'
import i18next from 'i18next'

import { Address } from './Fields/Address'
import { Checkbox } from './Fields/Checkbox'
import { Company } from './Fields/Company'
import { CreatedAt } from './Fields/CreatedAt'
import { Email } from './Fields/Email'
import { Name } from './Fields/Name'
import { Origin } from './Fields/Origin'
import { Phone } from './Fields/Phone'
import { RadioButton } from './Fields/RadioButton'
import { Title } from './Fields/Title'
import { Website } from './Fields/Website'

const getFormFieldValue = (item: Lead, fieldName: string) =>
  item.attributes.fields.find(
    field => field.name === fieldName || field.subtype === fieldName
  )?.value || '-'

const getFormFieldLabel = (item: Lead, fieldName: string) =>
  item.attributes.fields.find(
    field => field.name === fieldName || field.subtype === fieldName
  )?.label || ''

export interface LeadColumn extends Column<Lead> {
  adjustable?: boolean
  value?: boolean
}

const getColumns = (): LeadColumn[] => [
  {
    id: 'email',
    name: i18next.t('word.email'),
    value: true,
    width: '300px',
    render: (item: Lead) => <Email email={getFormFieldValue(item, 'email')} />
  },
  {
    id: 'origin',
    name: i18next.t('word.origin'),
    value: true,
    width: '250px',
    render: (item: Lead) => <Origin lead={item} />
  },
  {
    id: 'name',
    name: i18next.t('word.name'),
    value: true,
    width: '250px',
    render: (item: Lead) => <Name name={getFormFieldValue(item, 'name')} />
  },
  {
    id: 'phone',
    name: i18next.t('word.phone'),
    value: false,
    width: '200px',
    render: (item: Lead) => <Phone phone={getFormFieldValue(item, 'phone')} />
  },
  {
    id: 'checkbox-field',
    name: i18next.t('word.checkbox'),
    value: false,
    width: '200px',
    render: (item: Lead) => (
      <Checkbox
        value={getFormFieldValue(item, 'checkbox')}
        label={getFormFieldLabel(item, 'checkbox')}
      />
    )
  },
  {
    id: 'radio-button',
    name: i18next.t('word.radio.button'),
    value: false,
    width: '250px',
    render: (item: Lead) => (
      <RadioButton value={getFormFieldValue(item, 'radio')} />
    )
  },
  {
    id: 'website',
    name: i18next.t('word.website'),
    value: false,
    width: '250px',
    render: (item: Lead) => (
      <Website website={getFormFieldValue(item, 'website')} />
    )
  },
  {
    id: 'title',
    name: i18next.t('word.title'),
    value: false,
    width: '250px',
    render: (item: Lead) => <Title title={getFormFieldValue(item, 'title')} />
  },
  {
    id: 'company',
    name: i18next.t('word.company'),
    value: false,
    width: '250px',
    render: (item: Lead) => (
      <Company company={getFormFieldValue(item, 'company')} />
    )
  },
  {
    id: 'address',
    name: i18next.t('word.address'),
    value: false,
    width: '250px',
    render: (item: Lead) => (
      <Address address={getFormFieldValue(item, 'address')} />
    )
  }
]

export const dateColumn = {
  id: 'date',
  name: i18next.t('word.date'),
  value: true,
  width: '200px',
  render: (item: Lead) => (
    <CreatedAt createdAt={item.attributes.created_at_formatted} />
  )
}

const LOCAL_STORAGE_KEY = 'leadsColumns'

export const saveColumnsAtLocalStorage = (columns: LeadColumn[]) => {
  const saveObject = columns.map(column => ({
    id: column.id,
    value: column.value
  }))

  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(saveObject))
}

const getColumnsFromLocalStorage = (): LeadColumn[] => {
  const columns = localStorage.getItem(LOCAL_STORAGE_KEY)

  if (!columns) {
    return []
  }

  return JSON.parse(columns)
}

export const getInitialColumnsState = () => {
  const columns = getColumns()
  const savedColumns = getColumnsFromLocalStorage()

  if (!savedColumns.length) {
    return columns
  }

  return savedColumns.map(savedColumn => {
    const column = columns.find(column => column.id === savedColumn.id)

    return { ...column, value: savedColumn.value } as LeadColumn
  })
}
